import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const GIMP_TUTORIAL_PROJECT_ID = '9ce707a3-157d-40ac-807a-d18d5f1c1c89';

const GIMP_TUTORIAL_TASKS: TutorialTask[] = [
  {
    id: 'gimp-project-tour',
    name: 'Explore the labeling workspace',
    type: 'success',
  },
  {
    id: 'gimp-obvious-success',
    name: 'Obvious success',
    type: 'success',
    runId: '58d25e59-e363-408d-9104-9e162999ee6d',
  },
  {
    id: 'gimp-obvious-failure',
    name: 'Obvious failure',
    type: 'failure',
    runId: '711f0314-5bd4-4ee5-83dc-f7cfc9c9fb58',
  },
  {
    id: 'gimp-deceptive-failure',
    name: 'Fail that looks like success',
    type: 'failure',
    runId: 'e8549246-5fff-4738-83ac-f37c0ed216c4',
  },
  {
    id: 'gimp-deceptive-success',
    name: 'Success that looks like fail',
    type: 'success',
    runId: 'ed4e1a2f-3e7c-4d52-a073-b9f7fff119d2',
  },
  {
    id: 'gimp-judgement-success',
    name: 'Minor differences - success',
    type: 'success',
    runId: '37816b1a-e45b-42fb-bf05-34316fb42931',
  },
  {
    id: 'gimp-judgement-failure',
    name: 'Minor differences - failure',
    type: 'failure',
    runId: 'd8e605fc-14a5-42e0-9f49-f710c8f0a07a',
  },
];

const GIMP_TUTORIAL_STEPS: TutorialStep[] = [
  // ============================================================================
  // SHARED PROJECT TOUR FRAMES (frames 1-13)
  // Keep these identical across all tutorials
  // ============================================================================
  {
    id: 'gimp-project-welcome',
    title: 'Welcome to GIMP Labeling',
    content:
      'This tutorial gives you a quick tour of the labeling workspace. You can explore every part of the UI without affecting real data.\n\nOnce you claim a labeling project, you can click on the project to be brought to this page.\n\nIf you would like to exit the tutorial, just click "Exit Tutorial" on the top bar. You can always restart the tutorial by clicking "Show Tutorial."',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'gimp-problem-list',
    title: 'Assigned Problems',
    content:
      'Each card in this list represents a problem. The badge tracks how many runs you have labeled. Feel free to click through the list during the tutorial.',
    targetSelector: '.problem-list',
    action: 'explore',
    position: 'right',
    allowInteraction: true,
    scope: 'project',
  },
  {
    id: 'gimp-solution-upload',
    title: 'Solution Uploads',
    content:
      'Projects consist of two steps: first, you must upload the solution for the problem, and next, you should label all of the runs for that problem. When uploading your solution, make sure that your uploaded filename matches exactly the filename of the solution requested in the prompt.',
    targetSelector: '.solution-upload-section',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'gimp-bad-task-button',
    title: 'Mark Ambiguous Problems',
    content:
      'If the prompt is ambiguous or has multiple valid outcomes, mark it as a bad task here. Provide a concise reason so reviewers understand the issue. After marking as ambiguous, all runs will automatically be labeled and you will not have to upload a solution.',
    targetSelector: '.bad-task-btn',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'solution-upload-requirement',
    title: 'Solution Required',
    content:
      'Important: You must upload your solution before you can select and label any runs. This ensures you have solved the problem yourself before evaluating the AI\'s attempts. Notice that the runs below are currently grayed out.',
    targetSelector: '.run-list',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'solution-upload-select-run',
    title: 'Runs to Review',
    content:
      'Every run shows a model attempt. Now you can select one to open the viewer so you can study exactly how the agent approached the task. Click on any run below to proceed.',
    targetSelector: '.run-list',
    action: 'click',
    position: 'left',
    allowInteraction: true,
    scope: 'project',
    nextButtonLabel: 'Select a run to proceed',
  },
  {
    id: 'gimp-viewer-overview',
    title: 'Review Each Attempt',
    content:
      'The video displays the AI solving the task. The AI has a computer environment consisting of many applications like Audacity, LibreOffice, Inkscape, Gimp, and Shotcut, and can use any of these applications to solve the task. The AI can also use programming and bash scripting. Anything is fair game as long as the AI solves the task as requested!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-viewer-timeline',
    title: 'Timeline Controls',
    content:
      'Use the timeline to jump between frames. Speed controls and keyboard shortcuts help you inspect long attempts quickly without skipping critical moments.',
    targetSelector: '.timeline-container',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'gimp-viewer-command-panel',
    title: 'Command History',
    content:
      'The agent can launch shell commands, scripts, or custom tools while it works. Every command it runs appears here so you can verify exactly what code or terminal actions were executed.',
    targetSelector: '.tool-output-section:first-child',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-viewer-thoughts',
    title: 'Agent Notes',
    content:
      'Read the thought bubble to see why the agent acted. When judging ambiguity, these notes often reveal whether the prompt had multiple valid interpretations.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
  },
  {
    id: 'gimp-viewer-inspect',
    title: 'Inspect the Attempt',
    content:
      'Watch the video carefully to see every step the AI took. When you need the final output, right-click the video to download the files it produced and compare them with your ground-truth solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-viewer-evaluate',
    title: 'Label the Run',
    content:
      'Use this panel to decide whether the run succeeded or failed. If you realize that a problem is ambiguous or has multiple correct solutions, return to the problem workspace, mark it as a bad task, and then move on to the next problem.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    maxWidth: 360,
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'gimp-viewer-return',
    title: 'Return to the Problem Workspace',
    content:
      'Click "Back to Problem" when you finish reviewing runs. That returns you to the overview so you can upload a solution or mark the problem ambiguous.',
    targetSelector: '.back-to-problem-btn',
    action: 'click',
    position: 'right',
    scope: 'viewer',
    allowInteraction: true,
  },
  {
    id: 'gimp-project-recap',
    title: 'Wrap Up the Problem',
    content:
      'From this workspace you can revisit the prompt, upload the required solution file, and finish labeling every run. Ambiguous problems can be waived entirely.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'gimp-example-transition',
    title: 'Run Labeling Examples',
    content:
      'Now let us review real AI runs and decide whether each is a success or a failure. We will walk through several examples together.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },

  // ============================================================================
  // GIMP-SPECIFIC EXAMPLE STEPS
  // ============================================================================

  // Example 1: Obvious Success
  {
    id: 'gimp-obvious-success-intro',
    title: 'Obvious Success',
    content:
      'Here\'s an example of an obvious success. If you scroll through the video, you\'ll see that the AI clearly covers the 4th star and its shadow with all white, and exports the new file with the correct name.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-obvious-success-conclusion',
    title: 'Clear Success',
    content:
      'The AI completed the task exactly as requested. This is an obvious success. Mark it as a success and move on.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    seekFrame: 44,
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 2: Obvious Failure
  {
    id: 'gimp-obvious-failure-intro',
    title: 'Obvious Failure',
    content:
      'In this run, the AI clearly does not change the color of "Do" and "it" to red as requested. Watch the video carefully and you will see that the AI never completes the required color changes.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-obvious-failure-conclusion',
    title: 'Mark as Failure',
    content:
      'Because the AI never successfully changes the text colors to red as requested, this run is an obvious failure. Mark it as a failure and move on.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 3: Deceptive Failure (looks like success)
  {
    id: 'gimp-deceptive-failure-intro',
    title: 'Looks Like Success, But Check Carefully',
    content:
      'Sometimes, runs are less obvious. In this video, you might be tempted to label it a success—the AI successfully duplicates the arrow, rotates it by 45 degrees, and repeats 6 times as requested.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-deceptive-failure-download',
    title: 'Download to Verify',
    content:
      'However, it\'s hard to tell what each frame of the final gif looks like from the video alone. We should right-click the video and download the output file to confirm.',
    targetSelector: '.screenshot-wrapper',
    action: 'seek-frame',
    seekFrame: 77,
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-deceptive-failure-details',
    title: 'The Output Reveals the Truth',
    content:
      'If you download the output file, you will notice the AI did not use each individual layer as a frame in the gif. Instead, it combined all frames, so they accumulate on top of each other as the animation runs. The task specifically says to use each individual layer as a frame in the animation.',
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-deceptive-failure-conclusion',
    title: 'Always Download and Compare',
    content:
      'This demonstrates why you must always download and carefully examine the output file. What looks perfect in the video may have subtle but critical errors. This run is a failure.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 4: Deceptive Success (looks like failure)
  {
    id: 'gimp-deceptive-success-intro',
    title: 'Looks Like Failure, But Check the Output',
    content:
      'Conversely, sometimes you will have videos that look like failures but are actually successes! This often happens because the AI can write code that directly modifies files without using the GIMP UI.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-deceptive-success-details',
    title: 'Code Instead of UI',
    content:
      'If you scroll to the very last frame of the video, the file still looks all white and unchanged. However, if you download the output file, you will notice that the AI has actually successfully completed the problem! It wrote its own code instead of using GIMP.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 37,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'gimp-deceptive-success-conclusion',
    title: 'Output File is the Source of Truth',
    content:
      'Remember: the output file is the source of truth when deciding if the AI was correct or not. Always download it and compare it to your solution to double-check your label! This run should be labeled a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 5: Exercising Judgement - Success
  {
    id: 'gimp-judgement-success-intro',
    title: 'Exercising Judgement: Truly Negligible Differences',
    content:
      'Because the AI can write arbitrary code to solve problems instead of using GIMP, we will NOT require pixel-perfect equality. Sometimes, you should still label a run as success even if you identify a very minor difference between the AI output and your solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-judgement-success-heuristic',
    title: 'Extremely Tiny Differences Only',
    content:
      'To be clear, the difference needs to be extremely tiny. We are counting on you to exercise your best judgement as a raster graphics expert to determine when a difference should be considered truly negligible. As a heuristic, if someone can spot the difference within 10 seconds of looking at the image, I think you should consider the run a FAIL.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-judgement-success-details',
    title: 'Minor Pixel Differences',
    content:
      'Here\'s an example of a run where I think the difference is truly negligible. If you download the output file and compare it to the solution, you\'ll notice the AI\'s output has a few more blue and red specks at the bottom. It can\'t be more than 4 or 5 pixels.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 36,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'gimp-judgement-success-conclusion',
    title: 'Truly Negligible = Success',
    content:
      'Use your best judgement as a raster graphics expert to determine when a difference should be considered truly negligible. In this case, the difference is so tiny that this run should be labeled a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 6: Exercising Judgement - Failure
  {
    id: 'gimp-judgement-failure-intro',
    title: 'Exercising Judgement: Noticeable Differences',
    content:
      'In contrast, let us look at another run of the same problem. Download the AI output and compare it carefully to your solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-judgement-failure-details',
    title: 'Significant Fuzziness',
    content:
      'You will notice this output has significantly more black fuzziness around the statue than the solution, likely because the AI used a different threshold value. Though still objectively minor, this difference is large enough to be noticeable.',
    targetSelector: '.screenshot-wrapper',
    action: 'seek-frame',
    seekFrame: 34,
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'gimp-judgement-failure-conclusion',
    title: 'Noticeable Differences = Failure',
    content:
      'When in doubt about whether a difference is "small enough," exercise your best judgement and err on the side of marking any identified differences as fails. This run should be labeled a failure. Please feel free to send us questions if you\'re unsure about a particular video!',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Final Step
  {
    id: 'tutorial-complete',
    title: 'Nice Work!',
    content:
      'You have finished the GIMP tutorial. You now know how to tour the workspace, inspect outputs, download and compare images, and exercise judgement on subtle differences. Remember: always download and compare the output file—it is the source of truth. When the AI uses code instead of the UI, the video alone is not enough. Good luck on real projects!',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
  },
];

export const gimpTutorialConfig: TutorialConfig = {
  software: 'gimp',
  displayName: 'GIMP Labeling Tutorial',
  totalTasks: GIMP_TUTORIAL_TASKS.length,
  tasks: GIMP_TUTORIAL_TASKS,
  steps: GIMP_TUTORIAL_STEPS,
  completionKey: 'labelPlatform_gimp_TutorialCompleted',
  dataSource: 'api',
  projectId: GIMP_TUTORIAL_PROJECT_ID,
};
